/**
 * Scientific-sources list for a product→impact claim: the supporting articles
 * returned by the product-impact reasoning service, each with a claim-tied
 * insight, a relevance/scope badge, and a link out. Sibling of the chat MCP
 * `supporting-articles` iframe app, but typed for the platform-side
 * `Leena2ReasoningArticle` shape.
 */
import { Leena2ReasoningArticle } from '@upright/common/model/output/scores/drilldowns/leena2-impact-reasoning-types';
import styled from 'styled-components';

/** How directly the source matches the (product, impact) pair. */
function scopeLabel(scope: string): string {
  switch (scope) {
    case 'exact':
      return 'Direct match';
    case 'broader_product':
      return 'Broader product';
    case 'broader_impact':
      return 'Broader impact';
    case 'broader_both':
      return 'Related';
    default:
      return scope;
  }
}

function citation(article: Leena2ReasoningArticle): string {
  const authors = article.authors.length > 2 ? `${article.authors[0]} et al.` : article.authors.join(' & ');
  return [authors || null, article.journal, article.year ?? null].filter(Boolean).join(' · ');
}

export function ScientificSources({ articles }: { articles: Leena2ReasoningArticle[] }) {
  if (articles.length === 0) {
    return <Empty>No scientific sources found for this product–impact pair.</Empty>;
  }
  return (
    <List>
      {articles.map(article => {
        const meta = citation(article);
        return (
          <Card key={article.doi ?? article.url}>
            <TitleRow>
              {article.url ? (
                <SourceLink href={article.url} target="_blank" rel="noreferrer noopener">
                  {article.title}
                </SourceLink>
              ) : (
                <SourceTitle>{article.title}</SourceTitle>
              )}
              <ScopeBadge $scope={article.scope}>{scopeLabel(article.scope)}</ScopeBadge>
            </TitleRow>
            {meta && <Meta>{meta}</Meta>}
            {article.insight && <Insight>{article.insight}</Insight>}
          </Card>
        );
      })}
    </List>
  );
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__sm);
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space__sm) var(--space__md);
  border: 1px solid var(--color__gray-200);
  border-radius: var(--border-radius__md);
  background: var(--color__white);
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space__sm);
`;

const SourceTitle = styled.span`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-900);
`;

const SourceLink = styled.a`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  color: var(--color__gray-900);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ScopeBadge = styled.span<{ $scope: string }>`
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: var(--border-radius__full);
  font-size: var(--font-size__xs);
  white-space: nowrap;
  color: ${p => (p.$scope === 'exact' ? 'var(--color__teal-900)' : 'var(--color__gray-700)')};
  background: ${p => (p.$scope === 'exact' ? 'var(--color__teal-100)' : 'var(--color__gray-100)')};
`;

const Meta = styled.div`
  font-size: var(--font-size__xs);
  color: var(--color__gray-500);
`;

const Insight = styled.div`
  margin-top: 2px;
  font-size: var(--font-size__sm);
  color: var(--color__gray-700);
  line-height: 1.4;
`;

const Empty = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-500);
`;
